import { GeoMap, LatLng, LatLngLiteral, OverlayView } from 'bits/map'

interface MapConnectionLabelOptions {
  content: string
  location: LatLngLiteral
  type?: 'regular' | 'count'
}

const LABEL_CLASS_NAME_COUNT = 'map-connection-label count'
const LABEL_CLASS_NAME_REGULAR = 'map-connection-label'
class MapConnectionLabel extends OverlayView {
  private content: string
  private location: LatLngLiteral
  private type: 'regular' | 'count' = 'regular'
  private div: HTMLDivElement | null = null
  private color: string | null = null
  private backgroundColor: string | null = null

  public constructor(options: MapConnectionLabelOptions) {
    super()
    this.content = options.content
    this.location = options.location
    if (options.type) {
      this.type = options.type
    }
  }

  private get divClassName(): string {
    if (this.type === 'count') {
      return LABEL_CLASS_NAME_COUNT
    }
    return LABEL_CLASS_NAME_REGULAR
  }

  public setContent(content: string): void {
    this.content = content
    this.draw()
  }

  public setType(type: 'regular' | 'count'): void {
    this.type = type
    this.draw() // recompute and redraw with new class name
  }

  public setLocation(location: LatLngLiteral): void {
    this.location = location
    this.draw() // recompute and redraw at new location
  }

  public setColor({ backgroundColor, foregroundColor }): void {
    this.color = foregroundColor
    this.backgroundColor = backgroundColor
    this.draw()
  }

  public onAdd(): void {
    const div = document.createElement('div')
    div.innerText = this.content
    div.className = this.divClassName
    div.style.position = 'absolute'
    div.style.transform = 'translate(-50%, -50%)'
    div.style['z-index'] = 9999 // above the polylines which has `z-index: 30` as of Nov 2019
    const panes = this.getPanes()
    // Read more: https://developers.google.com/maps/documentation/javascript/customoverlays#initialize
    panes.overlayLayer.appendChild(div) // The same pane as the polyline.
    this.div = div
  }

  public draw(): void {
    if (!this.div) return
    this.div.className = this.divClassName
    const overlayProjection = this.getProjection()
    const pixelPosition = overlayProjection.fromLatLngToDivPixel(new LatLng(this.location))
    this.div.style.left = pixelPosition.x + 'px'
    this.div.style.top = pixelPosition.y + 'px'
    if (this.color) this.div.style.color = this.color
    if (this.backgroundColor) this.div.style.backgroundColor = this.backgroundColor
  }

  public onRemove(): void {
    if (!this.div) return
    this.div.parentNode ? this.div.parentNode.removeChild(this.div) : this.div.remove()
    this.div = null
  }

  public remove(): void {
    this.setMap(null)
  }

  public show(map: GeoMap | null): void {
    this.setMap(map)
  }

  public hide(): void {
    this.setMap(null)
  }
}

export default MapConnectionLabel
